import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Input,
  Button,
  Text,
  VStack,
  HStack,
  Image,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Link,
  Divider,
  Icon,
  useToast,
  Progress,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FaLeaf, FaRecycle, FaCheck, FaTimes } from 'react-icons/fa';
import { useRegister } from '../hooks/useApi';

// Password validation helper
const validatePassword = (password: string) => {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };
  
  const score = Object.values(checks).filter(Boolean).length;
  const strength = score < 2 ? 'weak' : score < 4 ? 'medium' : 'strong';
  
  return { checks, score, strength };
};

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPasswordHelp, setShowPasswordHelp] = useState(false);

  const passwordValidation = validatePassword(formData.password);

  const registerMutation = useRegister({
    onSuccess: (data) => {
      console.log('Registration successful:', data);
      toast({
        title: 'Registration Successful',
        description: `Welcome to ReDeuce, ${data.user.firstName}! Please check your email to verify your account.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      navigate('/login');
    },
    onError: (error: any) => {
      console.error('Registration error:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      
      let errorMessage = 'Failed to create account. Please try again.';
      
      if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Registration Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    
    // Enhanced password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else {
      const { checks } = passwordValidation;
      if (!checks.length) newErrors.password = 'Password must be at least 8 characters long';
      else if (!checks.uppercase) newErrors.password = 'Password must contain at least one uppercase letter';
      else if (!checks.lowercase) newErrors.password = 'Password must contain at least one lowercase letter';
      else if (!checks.number) newErrors.password = 'Password must contain at least one number';
      else if (!checks.special) newErrors.password = 'Password must contain at least one special character (!@#$%^&*(),.?":{}|<>)';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const registrationData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
      };
      
      console.log('Submitting registration data:', registrationData);
      registerMutation.mutate(registrationData);
    }
  };

  const getPasswordStrengthColor = () => {
    switch (passwordValidation.strength) {
      case 'weak': return 'red';
      case 'medium': return 'yellow';
      case 'strong': return 'green';
      default: return 'gray';
    }
  };

  return (
    <Box minH="calc(100vh - 200px)" py={12}>
      {/* Background gradient */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bgGradient="linear(135deg, brand.gray.50 0%, white 50%, brand.gray.50 100%)"
        zIndex="-1"
      />
      
      <Container maxW="6xl">
        <HStack spacing={16} align="start">
          {/* Left side - Branding */}
          <VStack 
            spacing={8} 
            flex="1" 
            align="start"
            display={{ base: 'none', lg: 'flex' }}
          >
            <VStack spacing={6} align="start">
              <Image
                src="/assets/logo/PNG/Logo_RGB.png"
                alt="ReDeuce Logo"
                h="80px"
                w="auto"
              />
              <VStack spacing={4} align="start">
                <Heading size="2xl" color="brand.green.dark">
                  Join the Sustainable Tennis Community
                </Heading>
                <Text fontSize="lg" color="brand.gray.600" lineHeight="1.7">
                  Be part of a movement that's changing how tennis players buy and sell equipment. 
                  Reduce waste, save money, and connect with fellow tennis enthusiasts who care about our planet.
                </Text>
              </VStack>
            </VStack>

            <VStack spacing={6} align="start" w="full">
              <HStack spacing={4}>
                <Icon as={FaLeaf} boxSize={6} color="brand.green.medium" />
                <VStack spacing={1} align="start">
                  <Text fontWeight="bold" color="brand.green.dark">Eco-Friendly Trading</Text>
                  <Text fontSize="sm" color="brand.gray.600">Give tennis equipment a second life</Text>
                </VStack>
              </HStack>
              <HStack spacing={4}>
                <Icon as={FaRecycle} boxSize={6} color="brand.green.medium" />
                <VStack spacing={1} align="start">
                  <Text fontWeight="bold" color="brand.green.dark">Circular Economy</Text>
                  <Text fontSize="sm" color="brand.gray.600">Buy, sell, repeat sustainably</Text>
                </VStack>
              </HStack>
            </VStack>
          </VStack>

          {/* Right side - Form */}
          <Box flex="1" maxW={{ base: 'full', lg: '500px' }}>
            <VStack spacing={8}>
              {/* Mobile logo */}
              <VStack spacing={4} display={{ base: 'flex', lg: 'none' }} textAlign="center">
                <Image
                  src="/assets/logo/PNG/Logo_RGB.png"
                  alt="ReDeuce Logo"
                  h="60px"
                  w="auto"
                />
                <Heading size="xl" color="brand.green.dark">Join ReDeuce</Heading>
              </VStack>

              <Box 
                bg="white" 
                p={8} 
                borderRadius="2xl" 
                shadow="xl"
                border="1px solid"
                borderColor="brand.gray.200"
                w="full"
              >
                <VStack spacing={6}>
                  <VStack spacing={2} textAlign="center" display={{ base: 'none', lg: 'flex' }}>
                    <Heading size="lg" color="brand.green.dark">Create Your Account</Heading>
                    <Text color="brand.gray.600">
                      Start your sustainable tennis journey today
                    </Text>
                  </VStack>

                  <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                    <VStack spacing={5}>
                      <HStack spacing={4} w="full">
                        <FormControl isInvalid={!!errors.firstName}>
                          <FormLabel color="brand.green.dark" fontWeight="semibold">
                            First Name
                          </FormLabel>
                          <Input
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            placeholder="Enter your first name"
                            borderColor="brand.gray.300"
                            _hover={{ borderColor: 'brand.green.light' }}
                            _focus={{ borderColor: 'brand.green.medium', boxShadow: '0 0 0 1px var(--chakra-colors-brand-green-medium)' }}
                            size="lg"
                          />
                          <FormErrorMessage>{errors.firstName}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors.lastName}>
                          <FormLabel color="brand.green.dark" fontWeight="semibold">
                            Last Name
                          </FormLabel>
                          <Input
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            placeholder="Enter your last name"
                            borderColor="brand.gray.300"
                            _hover={{ borderColor: 'brand.green.light' }}
                            _focus={{ borderColor: 'brand.green.medium', boxShadow: '0 0 0 1px var(--chakra-colors-brand-green-medium)' }}
                            size="lg"
                          />
                          <FormErrorMessage>{errors.lastName}</FormErrorMessage>
                        </FormControl>
                      </HStack>

                      <FormControl isInvalid={!!errors.email}>
                        <FormLabel color="brand.green.dark" fontWeight="semibold">
                          Email Address
                        </FormLabel>
                        <Input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="Enter your email address"
                          borderColor="brand.gray.300"
                          _hover={{ borderColor: 'brand.green.light' }}
                          _focus={{ borderColor: 'brand.green.medium', boxShadow: '0 0 0 1px var(--chakra-colors-brand-green-medium)' }}
                          size="lg"
                        />
                        <FormErrorMessage>{errors.email}</FormErrorMessage>
                      </FormControl>

                      <FormControl isInvalid={!!errors.password}>
                        <FormLabel color="brand.green.dark" fontWeight="semibold">
                          Password
                        </FormLabel>
                        <Input
                          name="password"
                          type="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          onFocus={() => setShowPasswordHelp(true)}
                          placeholder="Create a strong password"
                          borderColor="brand.gray.300"
                          _hover={{ borderColor: 'brand.green.light' }}
                          _focus={{ borderColor: 'brand.green.medium', boxShadow: '0 0 0 1px var(--chakra-colors-brand-green-medium)' }}
                          size="lg"
                        />
                        {formData.password && (
                          <Box mt={2}>
                            <HStack justify="space-between" mb={1}>
                              <Text fontSize="xs" color="gray.600">Password Strength:</Text>
                              <Text fontSize="xs" color={`${getPasswordStrengthColor()}.500`} fontWeight="bold">
                                {passwordValidation.strength.toUpperCase()}
                              </Text>
                            </HStack>
                            <Progress 
                              value={(passwordValidation.score / 5) * 100} 
                              colorScheme={getPasswordStrengthColor()}
                              size="sm"
                              borderRadius="md"
                            />
                          </Box>
                        )}
                        {showPasswordHelp && formData.password && (
                          <Box mt={3} p={3} bg="gray.50" borderRadius="md" border="1px" borderColor="gray.200">
                            <Text fontSize="xs" fontWeight="semibold" mb={2} color="gray.700">
                              Password Requirements:
                            </Text>
                            <List spacing={1}>
                              <ListItem fontSize="xs" color={passwordValidation.checks.length ? 'green.600' : 'gray.500'}>
                                <ListIcon as={passwordValidation.checks.length ? FaCheck : FaTimes} />
                                At least 8 characters
                              </ListItem>
                              <ListItem fontSize="xs" color={passwordValidation.checks.uppercase ? 'green.600' : 'gray.500'}>
                                <ListIcon as={passwordValidation.checks.uppercase ? FaCheck : FaTimes} />
                                One uppercase letter
                              </ListItem>
                              <ListItem fontSize="xs" color={passwordValidation.checks.lowercase ? 'green.600' : 'gray.500'}>
                                <ListIcon as={passwordValidation.checks.lowercase ? FaCheck : FaTimes} />
                                One lowercase letter
                              </ListItem>
                              <ListItem fontSize="xs" color={passwordValidation.checks.number ? 'green.600' : 'gray.500'}>
                                <ListIcon as={passwordValidation.checks.number ? FaCheck : FaTimes} />
                                One number
                              </ListItem>
                              <ListItem fontSize="xs" color={passwordValidation.checks.special ? 'green.600' : 'gray.500'}>
                                <ListIcon as={passwordValidation.checks.special ? FaCheck : FaTimes} />
                                One special character (!@#$%^&*)
                              </ListItem>
                            </List>
                          </Box>
                        )}
                        <FormErrorMessage>{errors.password}</FormErrorMessage>
                      </FormControl>

                      <FormControl isInvalid={!!errors.confirmPassword}>
                        <FormLabel color="brand.green.dark" fontWeight="semibold">
                          Confirm Password
                        </FormLabel>
                        <Input
                          name="confirmPassword"
                          type="password"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          placeholder="Confirm your password"
                          borderColor="brand.gray.300"
                          _hover={{ borderColor: 'brand.green.light' }}
                          _focus={{ borderColor: 'brand.green.medium', boxShadow: '0 0 0 1px var(--chakra-colors-brand-green-medium)' }}
                          size="lg"
                        />
                        <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
                      </FormControl>

                      <Button
                        type="submit"
                        size="lg"
                        width="full"
                        bg="brand.green.medium"
                        color="white"
                        _hover={{ bg: 'brand.green.dark' }}
                        _active={{ bg: 'brand.green.dark' }}
                        isLoading={registerMutation.isPending}
                        loadingText="Creating Account..."
                        mt={4}
                      >
                        Create Account
                      </Button>

                      <Text textAlign="center" fontSize="sm" color="brand.gray.600">
                        By creating an account, you agree to our{' '}
                        <Link color="brand.green.medium" fontWeight="semibold">
                          Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link color="brand.green.medium" fontWeight="semibold">
                          Privacy Policy
                        </Link>
                      </Text>

                      <Divider />

                      <Text textAlign="center" color="brand.gray.600">
                        Already have an account?{' '}
                        <Button 
                          variant="ghost" 
                          color="brand.green.medium" 
                          fontWeight="semibold"
                          onClick={() => navigate('/login')}
                          p={0}
                          h="auto"
                          minH="auto"
                        >
                          Sign in here
                        </Button>
                      </Text>
                    </VStack>
                  </form>
                </VStack>
              </Box>
            </VStack>
          </Box>
        </HStack>
      </Container>
    </Box>
  );
};

export default SignUpPage; 